import { uniqueId } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, Badge } from 'react-bootstrap';
import { Plus, X, PlayCircle } from 'react-bootstrap-icons';
import VideoPlayerModal from '../VideoPlayerModal';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../../helpers/api';
import { ENDPOINTS, API_URL } from '../../../helpers/constants';
import CircularProgressBar from '../circular-progress';

const getUploadedVideoUrl = filename => {
    const baseUrl = API_URL || window.location.origin;
    return `${baseUrl.replace(/\/$/, '')}${ENDPOINTS.PUBLIC_VIDEO(filename)}`;
};

const VideoUploader = ({ onFileSelect, disabled }) => (
    <div
        onClick={disabled ? undefined : onFileSelect}
        style={{
            width: '200px',
            height: '150px',
            border: '2px dashed #ccc',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: disabled ? '#f5f5f5' : '#fafafa',
            transition: 'all 0.3s ease'
        }}
        onMouseEnter={e => {
            if (!disabled) {
                e.currentTarget.style.borderColor = '#007bff';
                e.currentTarget.style.backgroundColor = '#f0f8ff';
            }
        }}
        onMouseLeave={e => {
            if (!disabled) {
                e.currentTarget.style.borderColor = '#ccc';
                e.currentTarget.style.backgroundColor = '#fafafa';
            }
        }}
    >
        <Plus size={30} color={disabled ? '#999' : '#007bff'} />
        <span style={{ marginTop: '8px', color: disabled ? '#999' : '#007bff', fontSize: '14px' }}>
            Add Video
        </span>
    </div>
);

const Video = ({
    videos = [],
    onVideosChange,
    isEditMode = true,
    containerClassName = '',
    showTitle = true,
    title = 'Virtual Tour Video',
    multiple = false
}) => {
    const inputRef = useRef();
    const [showPlayer, setShowPlayer] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);

    const processFiles = fileList => {
        if (!fileList?.length) return;
        const files = multiple ? Array.from(fileList) : [fileList[0]];
        const newVideos = files.map(file => ({
            file,
            status: 'WAITING_FOR_UPLOAD',
            id: uniqueId(),
            previewUrl: URL.createObjectURL(file),
            filename: file.name,
            name: file.name
        }));

        if (newVideos.length > 0) {
            const updatedVideos = multiple ? [...videos, ...newVideos] : newVideos;
            if (onVideosChange) onVideosChange(updatedVideos);
        }
    };

    const updateVideoStatus = (videoObject, status, response = {}) => {
        if (!onVideosChange) return;
        onVideosChange(prev =>
            prev.map(v => (v.id === videoObject.id ? { ...v, ...response, status } : v))
        );
    };

    const runVideoHttpUpload = async videoToBeUploaded => {
        try {
            const formData = new FormData();
            formData.append('video', videoToBeUploaded.file);

            const { error, response } = await makeApiRequests({
                requestBody: formData,
                endpoint: ENDPOINTS.DISK_VIDEO_UPLOAD,
                method: 'POST',
                stringify: false
            });

            if (error) {
                updateVideoStatus(videoToBeUploaded, 'ERROR');
                toast.error('Failed to upload video');
            } else {
                updateVideoStatus(videoToBeUploaded, 'UPLOADED', response);
                toast.success('Video uploaded successfully');
            }
        } catch {
            updateVideoStatus(videoToBeUploaded, 'ERROR');
            toast.error('Failed to upload video');
        }
    };

    const videosIdStatusKey = videos.map(video => `${video.id}:${video.status}`).join('|');

    useEffect(() => {
        const waiting = videos.filter(v => v.status === 'WAITING_FOR_UPLOAD' && v.file);
        if (!waiting.length) return;

        const waitingIds = new Set(waiting.map(v => v.id));
        if (onVideosChange) {
            onVideosChange(prev =>
                prev.map(v => (waitingIds.has(v.id) ? { ...v, status: 'UPLOADING' } : v))
            );
        }

        waiting.forEach(v => runVideoHttpUpload(v));
    }, [videosIdStatusKey]);

    const onRemoveVideo = videoObject => {
        if (videoObject.previewUrl && videoObject.previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(videoObject.previewUrl);
        }

        const updatedVideos = videos.filter(v => v.id !== videoObject.id);
        if (onVideosChange) onVideosChange(updatedVideos);
    };

    const validateFiles = files => {
        const fileArray = Array.from(files);
        const invalidFiles = fileArray.filter(file => !file.type.startsWith('video/'));

        if (invalidFiles.length > 0) {
            toast.error(`Only video files are allowed. Please select valid video files.`);
            return false;
        }
        return true;
    };

    const handleFileSelect = () => {
        inputRef.current.click();
    };

    const handleVideoClick = video => {
        const videoUrl = getVideoUrl(video);
        if (videoUrl && (videoUrl.startsWith('http') || videoUrl.startsWith('blob:') || videoUrl.startsWith('/'))) {
            setSelectedVideo(videoUrl);
            setShowPlayer(true);
        } else {
            toast.error('Video URL is not available');
        }
    };

    const getVideoUrl = video => {
        if (video.status === 'UPLOADED' && video.filename) {
            return getUploadedVideoUrl(video.filename);
        }
        if (video.url) {
            return video.url.startsWith('http') ? video.url : getUploadedVideoUrl(video.url);
        }
        return video.previewUrl;
    };

    return (
        <div className={containerClassName}>
            {showTitle && <h6 className="mb-3">{title}</h6>}
            <div
                style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                    e.preventDefault();
                    const dtFiles = e.dataTransfer?.files;
                    if (dtFiles && dtFiles.length > 0 && validateFiles(dtFiles)) {
                        processFiles(dtFiles);
                    }
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept="video/*"
                    multiple={multiple}
                    style={{ display: 'none' }}
                    onChange={e => {
                        const target = e?.target;
                        const targetFiles = target?.files;
                        if (targetFiles && targetFiles.length > 0 && validateFiles(targetFiles)) {
                            processFiles(targetFiles);
                        }
                        if (target) target.value = '';
                    }}
                />

                {isEditMode && (multiple ? videos.length === 0 : videos.length < 1) && (
                    <VideoUploader onFileSelect={handleFileSelect} disabled={!isEditMode} />
                )}

                {videos.map((video, index) => {
                    const videoUrl = getVideoUrl(video);
                    const isUploaded = video.status === 'UPLOADED';
                    const isUploading = video.status === 'UPLOADING';
                    const isError = video.status === 'ERROR';

                    return (
                        <Card
                            key={video.id || index}
                            style={{
                                width: '200px',
                                height: '150px',
                                position: 'relative',
                                cursor: 'pointer',
                                overflow: 'hidden'
                            }}
                        >
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#000',
                                    position: 'relative'
                                }}
                                onClick={() => handleVideoClick(video)}
                            >
                                {videoUrl && (
                                    <video
                                        src={videoUrl}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onError={e => {
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                )}
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        color: 'white',
                                        fontSize: '40px'
                                    }}
                                >
                                    <PlayCircle />
                                </div>
                                {isEditMode && (
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        style={{
                                            position: 'absolute',
                                            top: '5px',
                                            right: '5px',
                                            zIndex: 10
                                        }}
                                        onClick={e => {
                                            e.stopPropagation();
                                            onRemoveVideo(video);
                                        }}
                                    >
                                        <X size={16} />
                                    </Button>
                                )}
                                {isUploading && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                            color: 'white',
                                            padding: '5px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <CircularProgressBar size={1} />
                                        <span style={{ marginLeft: '10px', fontSize: '12px' }}>Uploading...</span>
                                    </div>
                                )}
                                {isError && (
                                    <Badge variant="danger" style={{ position: 'absolute', top: '5px', left: '5px' }}>
                                        Error
                                    </Badge>
                                )}
                                {isUploaded && (
                                    <Badge variant="success" style={{ position: 'absolute', top: '5px', left: '5px' }}>
                                        Uploaded
                                    </Badge>
                                )}
                            </div>
                        </Card>
                    );
                })}

                {isEditMode && multiple && videos.length > 0 && videos.every(v => v.status === 'UPLOADED' || v.status === 'ERROR') && (
                    <VideoUploader onFileSelect={handleFileSelect} disabled={!isEditMode} />
                )}
            </div>

            <VideoPlayerModal
                show={showPlayer}
                onClose={() => setShowPlayer(false)}
                videoUrl={selectedVideo}
                title="Virtual Tour Video"
            />
        </div>
    );
};

export default Video;
